import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { reilmAPI } from '../services/apiClient'
import SearchCriteria, { PROPERTY_TYPES } from '../features/arv/SearchCriteria'
import ArvMap from '../features/arv/ArvMap'
import ComparableFilterRow from '../features/arv/ComparableFilterRow'
import ComparableGrid from '../features/arv/ComparableGrid'
import EstimateMetrics from '../features/arv/EstimateMetrics'
import GalleryModal from '../components/GalleryModal'
import BackLink from '../components/BackLink'
import { galleryJustClosed } from '../components/galleryCooldown'
import PageShell from '../layouts/PageShell'
import AppNavbar from '../features/AppNavbar'
import { compDisplayPrice } from '../features/arv/compPrice'
import { parseStatusFilter, statusMatches } from '../features/arv/statusFilter'
import { boundValue, hasRecomputeFieldError } from '../features/arv/fieldLimits'
import { compAgeMonths } from '../features/arv/compAge'
import SearchProgress from '../features/arv/SearchProgress'

// Comp Quality — the card Микола renamed from Comps Strength. The thresholds are unchanged from
// what shipped: 85 / 80 / 75 / 70, which is what his own output matrix asks for. Two labels and four
// captions are what actually differ, so this is a substitution and not a new calculation — writing a
// new one would be work against code that already answers correctly.
//
// The estimate of 12.08 recorded the Figma matrix as starting at 90; the 18.08 extract reads 85, so
// he closed that himself (files/sow2/spec/arv.md section 8). 85-89.99 is Excellent.
function getCompQuality(avg) {
  if (avg === null) return { label: null, sub: null }
  if (avg >= 85) return { label: 'Excellent', sub: 'Highly reliable, closely matched comps' }
  if (avg >= 80) return { label: 'Strong',    sub: 'Reliable comps with minor differences' }
  if (avg >= 75) return { label: 'Moderate',  sub: 'Reasonable comps with some adjustments' }
  if (avg >= 70) return { label: 'Weak',      sub: 'Less reliable comps' }
  return           { label: 'Poor',     sub: 'Insufficient or weak comparable data' }
}

// Show: how many ranked rows the table draws. `Top 10` is the default his requirement names, and it
// is the same number as the backend's enrichment window (reilm-be utils/arvFetch.js
// INITIAL_SHOW_WINDOW) without being the same set of rows: the backend enriches the first ten of the
// UNFILTERED ranking, this cuts the first ten of baseFiltered, and a saved price, sqft or status
// bound - 21 of 34 saved analyses carry one - makes those two different rows. Keeping the numbers
// equal is what makes an unfiltered table open warm; a filtered one opens on rows past the server's
// window and tops them up one request at a time, which is what runTopUps below exists for.
//
// SHOW_ALL is -1 and not null: `filters.visible ?? 10` is how an absent value falls back to the
// default, and null would be swallowed by it. A separate boolean key was the other option and is not
// available — arv.filters in reilm-be modules/properties/model.js is a fixed set of seven keys, so a
// new one means a schema change, which this MR does not carry.
const SHOW_ALL = -1
const SHOW_STEPS = [10, 20]

// `All 48` in the mock is a mock. The number is the actual size of the analysis, settled in
// files/sow2/spec/arv.md section 9: four different counts appear across his file (48, 10, 20, >= 60)
// and none of them is a constant the product should hold.
function showOptions(total) {
  return [
    ...SHOW_STEPS.filter((n) => n < total).map((n) => ({ value: n, label: `Top ${n}` })),
    { value: SHOW_ALL, label: `All ${total}` },
  ]
}

// A stored `visible` the current menu cannot offer, mapped to one it can.
//
// Load More wrote these. It persisted its own position and added five per click, so production
// carries 15, 35 and 40 among the saved filter sets - 8 of 21, counted 22.08 - and this MR deletes
// the control that produced them. Nothing normalised the leftovers: the field fell back to its first
// option while the table went on cutting at the stored number, so it read `Top 10` over 15 rows and
// the requirement's own arithmetic (`Top 10, exclude 2 -> 8`) became `15 minus the excluded`. It is
// reachable with no legacy data at all - `visible: 20` against 17 filtered comps offers no Top 20.
//
// Snapping UP rather than down. The stored number is how many rows the user last had, and the case
// that decides it is `visible: 20` over 17 comps: up gives `All 17`, which is exactly the 17 rows
// `slice(0, 20)` already drew, so the label turns true and nothing on screen moves. Down would cut
// that table from 17 rows to 10 to fix a label.
//
// Render-time only, nothing is written back. That keeps a PATCH out of page load for eight
// properties, and it answers what a one-off migration could not: the option set depends on how many
// comps survive the filter row, so a stored 20 reads `All 17` while the user has the set narrowed
// and returns to `Top 20` when they widen it again. The consequence for anything downstream is that
// `filters.visible` as persisted is NOT guaranteed to be a menu value - read it through here.
function normaliseShow(stored, total) {
  const options = showOptions(total)
  if (options.some((o) => o.value === stored)) return stored
  return SHOW_STEPS.find((n) => n >= stored && n < total) ?? SHOW_ALL
}

const savedBound = (saved, key, fallback) => {
  if (!saved || !(key in saved)) return fallback
  return saved[key] == null ? '' : String(saved[key])
}

// The property schema stores labels ("Single Family", "", ...) and the select holds option values
// ('single_family'). Display only: a label maps to its option, anything without one to Single Family.
function propertyTypeOption(value) {
  const key = String(value ?? '').trim().toLowerCase().replace(/\s+/g, '_')
  return PROPERTY_TYPES.some(o => o.value === key) ? key : 'single_family'
}

function initCriteria(saved, subj) {
  return {
    propertyType: propertyTypeOption(saved?.propertyType != null ? saved.propertyType : subj?.propertyType),
    beds:         saved?.beds != null ? String(saved.beds) : String(subj?.beds ?? ''),
    baths:        saved?.baths != null ? String(saved.baths) : String(subj?.baths ?? ''),
    sqftMin:      saved?.sqftMin != null ? String(saved.sqftMin)
                    : subj?.sqft ? subj.sqft : '',
    yearBuiltMin: saved?.yearBuiltMin != null ? String(saved.yearBuiltMin)
                    : subj?.yearBuilt ? String(subj.yearBuilt) : '',
    // An absent key is a filter set that predates this field and takes the default; an explicit
    // null is a field the user cleared, and it has to come back cleared. `?? '12'` could not tell
    // those apart, so a cleared Time reopened at twelve months.
    monthsWithin: savedBound(saved, 'monthsWithin', '12'),
    radiusMiles:  savedBound(saved, 'radiusMiles', '1'),
  }
}

// One object for the initial state and the fetch below. Two entry paths used to build the default
// separately and disagreed, so the same property showed one ARV opened from the Property Dashboard
// and another after a reload.
//
// Sqft is empty, which is what spec section 4 asks for (Sqft `Min` - `Max`). It used to be a
// 60-140% band around the subject's own sqft, and that band is not a harmless default: measured on
// production 20.08, on the 11 properties that have comps and no saved filters it drops 8 of 36,
// 10 of 40, 20 of 40, 17 of 40. The ARV moves and nothing on screen says why. The band survived
// unnoticed because on the path that computed it from a property with no sqft it produced the
// string "NaN", which is truthy but compares false against every row — a filter that looked applied
// and dropped nothing. One production property still holds sqftMin: "NaN" from that.
//
// Price stays empty too, and mr-02 settled that rather than deferring it again. Spec section 4
// lists `250` under Дефолти because the field is drawn holding 250 — but a drawn value is not a
// stated default, and the discriminator is corroboration: Show has a canvas sentence of its own
// ("The default is Top 10."), Price has none, and the same spec row lists Sqft as `Min` - `Max`,
// i.e. empty. So that row mixes one real default with mock data, and 250 is the mock half. It also
// costs something to get wrong in a way Top 10 does not: a price floor drops comps out of the
// estimate on first open, silently, for every property with no saved filters.
//
// status stays the string `All` rather than the serialised full set. parseStatusFilter reads both,
// 12 of 27 production filter sets already hold it, and it is the one of the two a person reading
// the collection by hand can understand.
const DEFAULT_FILTERS = {
  status: 'All',
  priceMin: '',
  priceMax: '',
  sqftMin: '',
  sqftMax: '',
  archivedView: 'active',
  visible: 10,
}

// The fields a search actually travels on. Radius and Time are deliberately absent: since mr-02
// they narrow the answer locally and never reach the provider, so typing one must not offer to
// re-run a twenty-second search that would come back with the identical set of comps.
const SEARCH_CRITERIA_FIELDS = ['propertyType', 'beds', 'baths', 'sqftMin', 'yearBuiltMin']

const sameSearchCriteria = (a, b) =>
  SEARCH_CRITERIA_FIELDS.every(k => String(a?.[k] ?? '') === String(b?.[k] ?? ''))

// Radius and Time filter the fetched set, which is his decision and not an implementation
// shortcut: "Radius фіксований 1 миля, поле 0.4-1.0, нижчі значення фільтрують локально", and the
// canvas line over the whole group is "Update these fields to dynamically update the table upon
// every user new input".
//
// So a search asks for the widest set those fields can narrow. Radius was never anything else —
// the RentCast call in reilm-be utils/arvFetch.js has `radius: 1` written into it and has never
// read the payload's radiusMiles, so that field has been decorative since before this MR. Time did
// travel: `criteria.monthsWithin` drops comps outside the window before scoring, so sending 12
// makes the fetched set the widest the field can express and the bound below narrows it with no
// round trip.
const SEARCH_RADIUS_MI = 1
const SEARCH_MONTHS = 12

// Bounded retries on 409 RECOMPUTE_CONFLICT (mr-14) — the backend names this an expected, recoverable
// response, not a real error, and single-flight below makes it rare from one tab. A handful is
// enough to ride out sustained contention without spinning forever on something that never clears.
const RECOMPUTE_MAX_ATTEMPTS = 3

// One key per background-save path, not per field like PropertyDashboard.jsx's save(): recompute,
// criteriaFilters and filtersPersist each always write the same field set regardless of which
// control triggered them, so one shared key covers each. handleToggleArchived is the fourth path
// and is keyed per row instead (archived:${propertyId}) -- a shared key there would let one row's
// successful toggle clear the banner while a different row's failed rollback is still unsaved.
const SAVE_KEY_RECOMPUTE = 'recompute'
const SAVE_KEY_CRITERIA_FILTERS = 'criteriaFilters'
const SAVE_KEY_FILTERS = 'filtersPersist'

function applyBaseFilters(sourceComps, f, crit) {
  // Parsed once per call rather than per comp: parseStatusFilter runs a JSON.parse, and this
  // predicate is evaluated for every comp on every keystroke in the filter row.
  const statuses = parseStatusFilter(f.status)
  // Null means no bound, and that covers an empty field as well as one holding a value outside its
  // own limits — fieldLimits.js says why an illegal value filters nothing rather than being clamped.
  const radiusMax = boundValue('radius', crit?.radiusMiles)
  const monthsMax = boundValue('time', crit?.monthsWithin)
  return sourceComps.filter(c => {
    if (!statusMatches(c, statuses)) return false
    // Miles on both sides: distanceSubj is `6371 * c * 0.621371` in reilm-be utils/arvFetch.js,
    // kilometres converted to miles and rounded to two places, and the field is labelled
    // Radius (mi). An unknown distance trips no bound, the same rule as price and sqft below.
    if (radiusMax != null && Number.isFinite(c.distanceSubj) && c.distanceSubj > radiusMax) return false
    // compAgeMonths and not arithmetic written here: the Date column prints the same age through
    // the same module, so a row reading `6.7 mo` and a bound of 6 cannot end up on opposite sides
    // of the line from each other.
    if (monthsMax != null) {
      const months = compAgeMonths(c)
      if (months != null && months > monthsMax) return false
    }
    // An absent value is not a zero. `(c.price ?? 0) / 1000` and `c.specs?.sqft ?? 0` measured a
    // comp we know nothing about against the minimum and dropped it — 4 of 462 recorded RentCast
    // listings carry a real price and no squareFootage (arvFetch.js:183 maps that to
    // specs.sqft: null), so a comp with an unknown size vanished from the window and the estimate,
    // and its slot went to a lower-ranked comp. An unknown value now trips no
    // bound at all, and mr-02 kept that rather than reversing it: a bound the user typed is a
    // statement about a number, and a comp we have no number for is not evidence against it. The
    // radius and time bounds above follow the same rule for the same reason. Treating an unknown as
    // $0 and 0 sqft was never one of the two defensible answers.
    // compDisplayPrice, not c.price: the table, the map and the listing page all show sold_price
    // for a Sold comp, so filtering on the list price measured a number the user never sees.
    const shownPrice = compDisplayPrice(c)
    const pricek = Number.isFinite(shownPrice) ? shownPrice / 1000 : null
    if (f.priceMin && pricek != null && pricek < Number(f.priceMin)) return false
    if (f.priceMax && pricek != null && pricek > Number(f.priceMax)) return false
    const sqft = Number.isFinite(c.specs?.sqft) ? c.specs.sqft : null
    if (f.sqftMin && sqft != null && sqft < Number(f.sqftMin)) return false
    if (f.sqftMax && sqft != null && sqft > Number(f.sqftMax)) return false
    return true
  })
}

export default function Arv() {
  const { id } = useParams()
  const navigate = useNavigate()

  // No seeding from location.state: PropertyDashboard passes the property it loaded, and HashRouter
  // keeps that copy across F5 and Back, so after a Search it is the pre-search analysis (prod-risk
  // A2). Every field below starts empty and is filled from getProperty alone.
  const [property, setProperty] = useState(null)
  const [arvData, setArvData] = useState(null)
  const [loading, setLoading] = useState(true)
  // null, 'locked', 'not_found' or 'load_failed' -- PropertyDashboard's values and messages. A failed
  // load must not fall through to the default criteria and a live Search (prod-risk A3).
  const [loadError, setLoadError] = useState(null)
  const [criteria, setCriteria] = useState(() => initCriteria(null, {}))
  // The criteria the last search ran with, which is what decides whether the button offers to run
  // another. It cannot come from arv.criteria: that path is rewritten on every field blur, so after
  // one edit it holds the current values rather than the searched ones and would always compare
  // equal. Kept in memory instead, seeded from the loaded criteria — which is why a page opened
  // fresh reads "no change since the search" even if the user had edited a field and left without
  // searching. Closing that needs somewhere to keep the searched criteria apart from the current
  // ones, i.e. a key arv.filters and arv.criteria do not have, i.e. a schema change this MR does
  // not carry.
  const [searchedCriteria, setSearchedCriteria] = useState(() => initCriteria(null, {}))
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState(null)
  // Shared banner for the four background-save paths (PropertyDashboard.jsx's own pattern, reused
  // rather than reinvented). failedSavesRef is a ref, not state, for the same reason PropertyDashboard's
  // is: it only gates a clear that setSaveError already causes to render.
  const [saveError, setSaveError] = useState(null)
  const failedSavesRef = useRef(new Set())
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS }))
  // Read by handleSearch, which resolves long after the render that started it. See the comment there.
  const filtersRef = useRef(filters)
  useEffect(() => { filtersRef.current = filters }, [filters])
  // Same reasoning as filtersRef, read by handleSearch's post-search recompute (mr-14 fix 2): a
  // Search takes 17-20s, and a field edited during that wait has to be what gets recomputed against
  // once the fresh comps land, not the value criteria held when the search was clicked.
  const criteriaRef = useRef(criteria)
  useEffect(() => { criteriaRef.current = criteria }, [criteria])
  const [comps, setComps] = useState([])
  const [galleryPhotos, setGalleryPhotos] = useState(null)
  // Cooldown key for the open gallery, paired with galleryPhotos above rather than folded into one
  // object -- see galleryCooldown.js for why an id, not the photos array, is what the guard keys on.
  const [galleryCompId, setGalleryCompId] = useState(null)
  // Shared by both onGalleryOpen props below (map pin and grid row), so ArvMap's and
  // ComparableGrid's own click handlers need no change to pick up the cooldown guard. The
  // length check mirrors the one ArvMap's own handlePhotoClick and ListingDetails' openGallery
  // already carry — this was the one path missing it, and columns.js can pass an empty array.
  const handleGalleryOpen = (photos, id) => {
    if (photos.length > 0 && !galleryJustClosed(id)) {
      setGalleryPhotos(photos)
      setGalleryCompId(id)
    }
  }
  // Last SERVER-confirmed value for a row's archived flag, keyed by propertyId; what
  // handleToggleArchived's rollback restores instead of `!next`. Full reasoning (why not `!next`,
  // per-row keying, clearing on new search) in src/pages/README.md.
  const confirmedArchivedRef = useRef(new Map())

  useEffect(() => {
    reilmAPI.getProperty(id)
      .then(res => {
        const prop = res.data?.data?.property
        if (!prop) {
          setLoadError('not_found')
          return
        }
        setProperty(prop)
        const arv = prop?.arv ?? null
        setArvData(arv)
        // propertyType beside details: a never-searched property's Property Type starts from its
        // own stored type, normalised to a select option by initCriteria.
        const loadedCriteria = initCriteria(arv?.criteria ?? null, { ...prop?.details, propertyType: prop?.propertyType })
        setCriteria(loadedCriteria)
        setSearchedCriteria(loadedCriteria)
        // `setFilters` unconditionally, and the ?? branch is live. It used to be dead: the fallback
        // object was built and then discarded, because the guard on the next line was
        // `if (arv?.filters)` — the exact case the ?? cannot reach.
        setFilters(arv?.filters ?? { ...DEFAULT_FILTERS })
        // No arv.comps on this property (never searched) yields an empty list here — a property
        // that was never searched has no comps to show, not the sample data that used to fill this
        // branch.
        const loaded = arv?.comps?.length ? arv.comps : []
        setComps(loaded.map(c => ({ ...c })))
      })
      .catch(err => {
        console.error('getProperty error', err)
        const status = err?.response?.status
        // 401: services/config.js is already redirecting to #/login; the skeleton stays meanwhile.
        if (status === 401) return true
        const code = err?.response?.data?.error?.code
        if (status === 403 && (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_REQUIRED')) {
          setLoadError('locked')
          return
        }
        setLoadError(status === 404 ? 'not_found' : 'load_failed')
      })
      .then(keepSkeleton => { if (!keepSkeleton) setLoading(false) })
  }, [id])

  // All eight criteria fields together — used below only to build a Search request, where the
  // provider needs every one of them in the same call. Nothing writes arv.criteria from this any
  // more; see buildCriteriaFilterPayload and buildRecomputePayload for the two paths that split its
  // old job between them (mr-14).
  function buildPayload(c) {
    const num = (v) => (v === '' || v == null ? null : Number(v))
    return {
      propertyType: c.propertyType || null,
      beds:         num(c.beds),
      baths:        num(c.baths),
      sqftMin:      num(c.sqftMin),
      sqftMax:      num(c.sqftMax),
      yearBuiltMin: num(c.yearBuiltMin),
      // No `?? 12` and no `?? 1` any more. They were right while these two were search parameters,
      // where an empty field had to mean the default; now the field is a filter and an empty one
      // means no bound, which is what boundValue reads it as. Keeping the fallback made the two
      // ends disagree: clearing Time widened the table on the keystroke and the next load snapped
      // it back to twelve months, because 12 is what had been stored.
      monthsWithin: num(c.monthsWithin),
      radiusMiles:  num(c.radiusMiles),
    }
  }

  // What gets sent to the provider: the same object with the window opened to its widest. Anything
  // narrower would be answered by a second search, and the two fields that would narrow it are the
  // ones the filter row now applies locally on every keystroke.
  function buildSearchPayload(c) {
    return { ...buildPayload(c), monthsWithin: SEARCH_MONTHS, radiusMiles: SEARCH_RADIUS_MI }
  }

  // The four fields PATCH /:id still owns after mr-14: propertyType, sqftMax, monthsWithin and
  // radiusMiles are filter/search-parameter state that PATCH /:id/arv/criteria never reads, so they
  // keep travelling through the general PATCH exactly as before. beds/baths/sqftMin/yearBuiltMin are
  // deliberately absent — see buildRecomputePayload. Derived from buildPayload rather than
  // duplicating its field list, so the two cannot silently drift on what "the rest" means.
  function buildCriteriaFilterPayload(c) {
    const { beds, baths, sqftMin, yearBuiltMin, ...rest } = buildPayload(c)
    return rest
  }

  // The four fields PATCH /:id/arv/criteria owns exclusively. Before mr-14 these travelled through
  // buildPayload/handleCriteriaPersist alongside the other four, which made the general PATCH and
  // this endpoint both writers of the same fields with nothing coordinating them — measured
  // producing a persisted state where the criteria on screen and the scores in the table came from
  // two different edits (13% of races). This endpoint is now their only writer from anywhere in
  // this screen; searchArvService still writes all eight wholesale on a Search, which is intended —
  // a Search legitimately replaces the whole saved analysis, criteria included.
  function buildRecomputePayload(c) {
    const num = (v) => (v === '' || v == null ? null : Number(v))
    return {
      beds:         num(c.beds),
      baths:        num(c.baths),
      sqftMin:      num(c.sqftMin),
      yearBuiltMin: num(c.yearBuiltMin),
    }
  }

  const handleSearch = async () => {
    // Re-entry guard: three clicks in the same task (faster than a render cycle) all pass through
    // before `disabled` ever reaches the DOM, each firing its own provider-backed search otherwise.
    if (searchInFlight.current) return
    setSearching(true)
    // A ref beside the state, and it has to be a ref: runTopUps closes over its render, so a state
    // flag would be the value at the moment the drain started rather than the value now.
    searchInFlight.current = true
    setSearchError(null)
    let failed = false
    try {
      const res = await reilmAPI.searchArvComps(id, buildSearchPayload(criteria))
      const arv = res.data?.data?.arv ?? null
      setArvData(arv)
      // The click-time snapshot, and here that is the right one — unlike filtersRef below, which
      // deliberately reads the live value. This records what the search was actually run with, so a
      // criterion edited during those twenty seconds leaves the button enabled rather than reading
      // as already searched.
      setSearchedCriteria(criteria)
      // Puts the user's own Radius and Time back over what the search just stored, and it is not
      // belt and braces — without it this MR loses them. searchArvService (reilm-be
      // modules/properties/service.js) writes `'arv.criteria': criteria` from the request body
      // wholesale, and the body buildSearchPayload sends carries the widened window rather than the
      // user's values. So a user sitting on Radius 0.6 who edits Beds and presses Update Analysis
      // keeps 0.6 on screen for the rest of the session and finds 1 on the next load, with the
      // comps between 0.6 and 1 mile back and nothing saying why. Two writes rather than one is the
      // price of the widened window; the alternative is the backend learning to store a criterion
      // it was not sent, which is a larger change than this MR carries.
      //
      // persistCriteriaFilters, not the full handleCriteriaPersist: this call only ever owned
      // propertyType/sqftMax/monthsWithin/radiusMiles (buildCriteriaFilterPayload), and the search
      // above already wrote all eight wholesale via searchArvService — this restores the four the
      // search's own widened window just overwrote. The scoring half is queued separately, below.
      //
      // criteriaRef.current, not the `criteria` closure above — every control in this panel stays
      // live for the full 17-20s a Search takes (same reasoning as filtersRef above), so a Radius or
      // Time edit blurred mid-flight has already persisted its own correct value by the time this
      // line runs. Reading the closure here was mr-14's own data-loss bug: it wrote the click-time
      // value back over whatever that mid-flight blur had just saved, on the exact call this
      // comment's opening paragraph exists to stop from widening Radius/Time back — it silently
      // destroyed the edit instead. The ref converges to the same value as the closure whenever
      // nothing was edited during the wait, so this changes nothing for that, the common, case.
      persistCriteriaFilters(criteriaRef.current)
      setComps((arv?.comps ?? []).map(c => ({ ...c })))
      // A search replaces the comp set, which makes archivedView and visible meaningless: they are
      // view state ABOUT a specific result set, not criteria. Only visible was reset here, and only
      // in local state, so a property last left on the Hidden pill came back from a search - and
      // from every later reload - showing zero rows while all three metric cards described the full
      // fresh set. That inconsistency is what makes this a defect and a stale `status: 'Sold'` not
      // one: status also drops the comps from baseFiltered, so the cards and the rows still agree. Persisting matters as much as resetting - the symptom users hit
      // was after a reload, which reads arv.filters back from the server. It is deliberately the
      // only two keys: price, sqft and status stay meaningful against a new set of comps.
      //
      // filtersRef, not the `filters` closure: this handler is awaiting a search that takes 17-20
      // seconds, and every control on the screen stays live for all of it. The two `searching`
      // overlays are `pointer-events-none` (below, over the map and over the grid), so they dim and
      // guard nothing - the filter row, the Show dropdown and every per-row toggle are all clickable
      // throughout. Spreading the click-time snapshot would revert - locally and on the server -
      // any bound the user typed while waiting.
      const nextFilters = { ...filtersRef.current, archivedView: 'active', visible: 10 }
      setFilters(nextFilters)
      handleFiltersPersist(nextFilters)
      // fetchAndEnrichComps, which the search just ran, scores every comp against property.details —
      // the property's own stored characteristics — never against criteria, even though criteria
      // shaped the provider fetch itself. recomputeArvService scores the same saved comps against
      // criteriaAsSubject instead. Those are two different scoring subjects, so the comps this
      // search just saved are sitting on screen scored the details way, one field blur away from
      // jumping to the criteria way with nothing on screen explaining the jump. Queuing one
      // recompute now, against whatever is currently in the panel, reconciles the two onto a single
      // source: displayed scores are criteria-based whether the user just searched or just edited a
      // field, and it costs no provider call, since recompute never makes one.
      //
      // criteriaRef.current, not the `criteria` closure above: a field edited during the 17-20s this
      // search was in flight must recompute against what is typed now, not what was typed at click
      // time. Reuses queueRecompute/runRecompute exactly as a blur does — see runRecompute's own
      // comment for why calling it from inside this still-in-flight try block is safe rather than
      // racy: searchInFlight.current is still true here, so this call only loads pendingRecompute
      // and returns without running; the runRecompute() in the finally block below, once
      // searchInFlight clears, is what actually drains it.
      //
      // searchedCriteria (set above, from the click-time snapshot) is deliberately left as is here.
      // Its only job is to say whether the comps ON SCREEN were fetched with the criteria now typed —
      // which is what decides whether Update Analysis offers to re-fetch — and recompute never
      // fetches, it only re-scores the same saved set, so it has nothing to say about that question.
      // If nothing was edited during the search, criteriaRef.current equals the criteria just
      // searched with, so criteriaDirty already reads false from setSearchedCriteria above and this
      // recompute changes nothing about that. If a field WAS edited mid-flight, that edit's own
      // handleCriteriaPersist already marked criteriaDirty true independent of this call, correctly:
      // a fresher fetch is warranted then, and this recompute only keeps the scores honest while the
      // user decides whether to run it.
      //
      // Cleared unconditionally, before the gate below, not only inside runRecompute's own failure
      // branch: setComps above just replaced the whole comp set, so whatever `lastSentRecomputeBody`
      // (mr-14 fix 4) held from before this Search no longer describes what is on screen, regardless
      // of whether the gate lets this reconciliation through. Left standing, a field made invalid
      // mid-flight (skipping the gate below) and then fixed back to the exact value it held before
      // the Search would reconstruct a body byte-identical to the pre-Search one — fix 4's own guard
      // would then read that as "already sent" and skip it, leaving the table on the Search's
      // details-scored comps with no trigger left to reconcile them short of a reload.
      lastSentRecomputeBody.current = null
      // hasRecomputeFieldError gate, mirroring handleCriteriaPersist's own: a field edited
      // mid-flight to an out-of-range value has no business reaching the recompute endpoint just
      // because a Search happened to be running when it was typed — the endpoint 400s the whole
      // body regardless of which call sent it.
      //
      // Clearing pendingRecompute rather than only skipping queueRecompute on the invalid branch:
      // an edit queued before this Search started stays paused in there while searchInFlight is
      // true (see runRecompute's own comment), untouched by an invalid edit typed afterward since
      // that edit never reaches this gate through a blur. Left standing, the finally block below
      // drains it once searchInFlight clears, sending a body built from before this Search against
      // the fresh comps it just replaced — describing a screen that, right now, shows an invalid
      // field and asked for nothing to be sent at all.
      // arv?.comps?.length gate, same precondition as hasSavedComps above (this response is the
      // freshest read of it): a zero-comparable search has nothing for the endpoint to recompute
      // against, so queuing here would fire the exact wasted round trip hasSavedComps exists to
      // stop a blur from making.
      if (hasRecomputeFieldError(criteriaRef.current)) {
        pendingRecompute.current = null
      } else if (arv?.comps?.length) {
        queueRecompute(criteriaRef.current)
      } else {
        pendingRecompute.current = null
      }
    } catch (err) {
      failed = true
      setSearchError(err.response?.data?.error?.message ?? 'Search failed. Please try again.')
    } finally {
      searchInFlight.current = false
      setSearching(false)
      // Drains whatever queueRecompute just loaded above on a success — or, on a failure, whatever a
      // mid-search edit queued while runRecompute's own loop was paused (searchInFlight.current) —
      // against the fresh, just-committed comps either way, since a failed search leaves comps and
      // criteria exactly as they were.
      //
      // Deliberately unconditional, unlike the hasRecomputeFieldError gate above: whatever is sitting
      // in pendingRecompute here was queued by an earlier blur that was already valid at the time it
      // queued, not by whatever the panel currently shows. A field the user is mid-edit on right now
      // (typed, not yet blurred) can be invalid without that draft ever having reached pendingRecompute
      // at all, so gating this drain on the panel's present validity would drop a queued, already-valid
      // edit to protect against a draft that was never queued in the first place.
      runRecompute()
      // Only after a failure. A search that succeeded replaces the comp set, and the two effects
      // below do the restart properly: the first wipes the queue and requestedTopUps against the new
      // searchedAt, the second re-queues from the rows now on screen. Calling runTopUps here on the
      // success path would beat both of them to it - the state updates above are still queued - and
      // drain ids belonging to the analysis that was just replaced.
      //
      // A failed search changes neither comps nor searchedAt, so neither effect fires and nothing
      // else would ever restart the drain: the rows the user is looking at would stay bare until
      // they left the screen.
      if (failed) runTopUps()
    }
  }

  // The optimistic update stands; the response is not read back into the table.
  //
  // This is the lag Микола reported ("if you exclude many comps quickly, there's a delay after each
  // exclusion"), and it was not the network being slow. Every response carries the whole saved
  // arv.comps array and this handler used to install it wholesale. Exclude two rows in quick
  // succession and response A — assembled before B's write committed — lands second-guessing B and
  // puts B's row back; B's own response then arrives and removes it again. The visible result is a
  // row flickering back for as long as the round trip takes, once per click.
  //
  // So the fix is not a debounce. A debounce would only make the same wrong write land later. What
  // the server echo can legitimately tell us here is nothing the table does not already know: the
  // request carries one boolean about one row, the backend writes exactly that with a positional
  // $set (updateCompService), and no other field moves -- so the confirmed-value write below reads
  // this row's flag out of the response only to match confirmedSavesRef's own convention
  // (PropertyDashboard.jsx, README.md); it is provably identical to `next` on every real request
  // today, kept as the fallback for a response shape that omits it.
  //
  // The rollback restores confirmedArchivedRef's value, not `!next` -- see that ref's own comment.
  const handleToggleArchived = async (row) => {
    const next = !row.archived
    const key = `archived:${row.propertyId}` // per row -- see the SAVE_KEY_* comment above for why
    const gen = nextSaveGeneration(key)
    if (!confirmedArchivedRef.current.has(row.propertyId)) {
      confirmedArchivedRef.current.set(row.propertyId, row.archived)
    }
    setComps(prev => prev.map(c =>
      c.propertyId === row.propertyId ? { ...c, archived: next } : c
    ))
    try {
      const res = await reilmAPI.toggleArvComp(id, row.propertyId, next)
      const confirmed = res?.data?.data?.arv?.comps?.find(c => c.propertyId === row.propertyId)?.archived
      confirmedArchivedRef.current.set(row.propertyId, confirmed ?? next)
      markSaveOk(key, gen)
    } catch (err) {
      // Gated the same as the banner: a stale failure's rollback predates whatever a newer save on
      // this row has already done, and applying it would silently overwrite that outcome. Read here,
      // not captured before the request went out: a DIFFERENT request on this same row (an earlier
      // one, superseded by this one's own gen) can succeed and advance confirmedArchivedRef while
      // this one is still in flight, and the value to roll back to is whatever the server holds NOW,
      // not what it held when this request was issued.
      if (isCurrentSave(key, gen)) {
        const confirmedValue = confirmedArchivedRef.current.has(row.propertyId)
          ? confirmedArchivedRef.current.get(row.propertyId)
          : !next
        setComps(prev => prev.map(c =>
          c.propertyId === row.propertyId ? { ...c, archived: confirmedValue } : c
        ))
      }
      markSaveFailed(key, err, gen)
    }
  }

  // Single-flight discipline for PATCH /:id/arv/criteria (mr-14) — a correctness requirement, not
  // just good practice. The backend's own concurrency fix guarantees the document never corrupts
  // under overlapping recomputes, but it explicitly does not guarantee two overlapping requests land
  // in send order: measured, even one user typing across the four fields at a normal pace can commit
  // out of order (each request 1-1.8s). Refs rather than state: a rapid blur-then-blur must see "one
  // already running" synchronously, not after a render commits.
  const recomputeRunning = useRef(false)
  // The one coalesced follow-up, or null. Always overwritten with the LATEST full criteria seen
  // while a recompute is running — three edits during one flight produce exactly one follow-up call,
  // carrying the third edit's values, not the first's.
  const pendingRecompute = useRef(null)
  // The serialized body of the last recompute request actually SENT (mr-14 fix 4) — set in
  // runRecompute right before the request goes out, never when one is merely queued. Radius and Time
  // blur through the same onCriteriaBlur handler as the four scoring fields despite never appearing
  // in buildRecomputePayload, so two Radius/Time blurs in a row produced two byte-identical bodies —
  // each a full aggregation pass over every saved comp and an unnecessary arv.rev bump, for no change
  // at all. Compared against what was SENT rather than against pendingRecompute itself: the latter
  // would break coalescing, since a queued-but-not-yet-sent edit has to stay queued even if a later
  // edit reverts to the same value only after something else changed in between.
  const lastSentRecomputeBody = useRef(null)

  // Merges a successful recompute's comps into local state the same way a Search does (setArvData +
  // setComps), so the table re-sorts and the estimate recalculates with no click and no reload.
  //
  // `archived` is kept from local state rather than the response, for the same reason runTopUps'
  // FROZEN_ON_TOPUP keeps it: this endpoint never writes archived, but a toggle whose own PATCH is
  // still in flight when this response lands must not be reverted by a server snapshot that predates
  // it — handleToggleArchived only rolls its optimistic update back on failure, it never re-applies
  // on success, so an overwritten archived here would stand until the next reload.
  //
  // Server order stands (arv.comps already comes back compSortOrder'd) — this maps over it rather
  // than sorting, so nothing here can fight that order.
  const applyRecomputeResult = (arv) => {
    if (!arv) return
    setArvData(arv)
    setComps(prev => {
      const byId = new Map(prev.map(c => [c.propertyId, c]))
      return (arv.comps ?? []).map(fresh => {
        const local = byId.get(fresh.propertyId)
        return local ? { ...fresh, archived: local.archived } : fresh
      })
    })
  }

  // Per-key generation counter: gates markSaveOk/markSaveFailed against a stale settlement acting on
  // a key a newer save already owns. Ported to PropertyDashboard.jsx, whose own remount wrapper
  // (PropertyDashboardRoute in App.js, not this file's ArvRoute) needs no manual clear for the same reason -- see src/pages/README.md.
  const saveGenerationsRef = useRef(new Map())

  const nextSaveGeneration = (key) => {
    const gen = (saveGenerationsRef.current.get(key) ?? 0) + 1
    saveGenerationsRef.current.set(key, gen)
    return gen
  }

  const isCurrentSave = (key, gen) => saveGenerationsRef.current.get(key) === gen

  // Same 401/403 carve-out as PropertyDashboard.jsx's save(): the shared axios instance already
  // redirects on a failed refresh, and ExpiredModal already covers the screen on a subscription
  // lapse, so neither is "failing" in the sense this banner exists for.
  const markSaveFailed = (key, err, gen) => {
    if (gen !== undefined && !isCurrentSave(key, gen)) return
    if (err?.response?.status === 401) return
    const code = err?.response?.data?.error?.code
    if (err?.response?.status === 403 && (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_REQUIRED')) return
    // NO_SAVED_COMPS answers about the server's own comp array, not this edit. Both queueing
    // decisions that feed the recompute drain (hasSavedComps here, arv?.comps?.length in
    // handleSearch's own post-search reconciliation) are gated on comps existing, and the drain
    // itself (runRecompute) is unconditional -- see its own comment. On a zero-comparable search it
    // drains nothing only because the queueing site already cleared it; what still reaches this 404
    // is a stale client whose local state still reads comps as existing while the server disagrees.
    if (err?.response?.status === 404 && code === 'NO_SAVED_COMPS') return
    failedSavesRef.current.add(key)
    setSaveError("We couldn't save your changes. Please try again.")
  }

  const markSaveOk = (key, gen) => {
    if (gen !== undefined && !isCurrentSave(key, gen)) return
    failedSavesRef.current.delete(key)
    if (failedSavesRef.current.size === 0) setSaveError(null)
  }

  // The queue drain. Loops rather than recursing on its own tail so a chain of coalesced follow-ups
  // cannot grow the call stack, and holds recomputeRunning for the whole loop — including across 409
  // retries — so a blur arriving mid-retry queues into pendingRecompute exactly like a blur arriving
  // during the first attempt.
  //
  // Paused while a Search is running (searchInFlight, same ref runTopUps pauses on and for a related
  // but distinct reason). The backend closes the SERVER-side half of this race on its own —
  // searchArvService bumps arv.rev on every Search, so a recompute whose in-flight read predates a
  // Search's own write already misses its conditional write and retries against a fresh read. What
  // that does not close is the CLIENT race: a Search takes 17-20s and a recompute a couple, so a
  // recompute queued mid-search almost always finishes and paints correct scores first, only for the
  // Search's own much slower response to land afterward and overwrite them with scores computed from
  // whatever criteria stood before the user's edit (handleSearch's setComps runs unconditionally).
  // Pausing the drain here and resuming it from handleSearch's own finally block, once the fresh
  // comps are in, orders it correctly instead: Search lands, then the queued edit recomputes against
  // it.
  const runRecompute = async () => {
    if (recomputeRunning.current) return
    recomputeRunning.current = true
    try {
      while (pendingRecompute.current && !searchInFlight.current) {
        const current = pendingRecompute.current
        pendingRecompute.current = null
        for (let attempt = 0; attempt < RECOMPUTE_MAX_ATTEMPTS; attempt++) {
          try {
            const payload = buildRecomputePayload(current)
            // Recorded on every attempt, retries included — they all resend the same `current`, so
            // this never changes value across a retry chain, only across a genuinely new one.
            lastSentRecomputeBody.current = JSON.stringify(payload)
            const res = await reilmAPI.recomputeArvCriteria(id, payload)
            applyRecomputeResult(res.data?.data?.arv ?? null)
            markSaveOk(SAVE_KEY_RECOMPUTE) // no gen -- recomputeRunning above rules out two in-flight recomputes on this key
            break
          } catch (err) {
            const code = err?.response?.data?.error?.code
            const canRetry = code === 'RECOMPUTE_CONFLICT' && attempt < RECOMPUTE_MAX_ATTEMPTS - 1
            if (!canRetry) {
              // 404 NO_SAVED_COMPS is gated out of ever being QUEUED, on both call sites that feed
              // this drain (hasSavedComps here, arv?.comps?.length in handleSearch's own post-search
              // reconciliation) -- but the drain itself is unconditional (see runRecompute's own
              // comment), so a stale client whose local state still reads comps as existing, while
              // the server disagrees, still reaches this catch. It is carved out of the banner in
              // markSaveFailed itself, not here — unlike a validation 400 or a network failure,
              // neither of which is self-resolving the way a 409 is, and both of which still raise
              // the shared save-error banner below.
              console.error('ARV criteria recompute failed', err)
              markSaveFailed(SAVE_KEY_RECOMPUTE, err) // no gen -- same single-flight guarantee as the success branch above
              // Cleared, not left standing (mr-14 fix 4's own guard, logic-auditor-caught): the ref
              // means "this body's answer is what the table currently shows," and a failure here
              // means it is not — the table still holds whatever scores it had before this attempt.
              // Left uncleared, any later blur that happens to reproduce this same failed body (a
              // Radius/Time edit, or the identical value retyped) would read it as already-sent and
              // skip resending it, with no other trigger left to recover the stale scores short of a
              // reload.
              lastSentRecomputeBody.current = null
              break
            }
            // Retries stick with `current` rather than picking up whatever landed in
            // pendingRecompute in the meantime — folding a newer edit in here and clearing the
            // queue loses it for good if this same attempt later exhausts its retries or hits a
            // non-409 failure, since nothing is left to trigger another turn of the outer loop.
            // Leaving it queued costs nothing: the outer loop picks it up next with its own full
            // retry budget the moment this attempt finishes, one way or another.
          }
        }
      }
    } finally {
      recomputeRunning.current = false
    }
  }

  const queueRecompute = (updatedCriteria) => {
    pendingRecompute.current = updatedCriteria
    runRecompute()
  }

  // Writes propertyType/sqftMax/monthsWithin/radiusMiles through the general PATCH — unaffected by
  // mr-14, see buildCriteriaFilterPayload. Four leaf paths ('arv.criteria.propertyType' etc.), not
  // one whole-object 'arv.criteria' entry: UPDATABLE_PROPERTY_PATHS (reilm-be
  // modules/properties/validation.js) stopped accepting the whole-object shape because
  // updatePropertyService's write is a plain `$set`, which replaces the WHOLE nested object rather
  // than merging it — a body naming only these four silently deleted the other four leaves
  // (beds/baths/sqftMin/yearBuiltMin) from arv.criteria on every save, measured on a scratch cluster
  // by the review pass that found it. Same pattern PropertyDashboard.jsx already uses for
  // details.beds/details.baths/details.sqft — a literal dotted key per field, not a nested value.
  const persistCriteriaFilters = (updatedCriteria) => {
    const { propertyType, sqftMax, monthsWithin, radiusMiles } = buildCriteriaFilterPayload(updatedCriteria)
    // Taken before the request goes out, not after: two blurs in quick succession must not share a
    // generation, or the second could never be told apart from the first once both are in flight.
    const gen = nextSaveGeneration(SAVE_KEY_CRITERIA_FILTERS)
    reilmAPI.updatedPropertiesDetails(id, {
      'arv.criteria.propertyType': propertyType,
      'arv.criteria.sqftMax': sqftMax,
      'arv.criteria.monthsWithin': monthsWithin,
      'arv.criteria.radiusMiles': radiusMiles,
    })
      .then(() => markSaveOk(SAVE_KEY_CRITERIA_FILTERS, gen))
      .catch(err => {
        console.error('criteria persist', err)
        markSaveFailed(SAVE_KEY_CRITERIA_FILTERS, err, gen)
      })
  }

  // Bound to every onCriteriaBlur in SearchCriteria and ComparableFilterRow — Property Type's
  // onChange included, since it fires through the same prop. Persists the non-score fields
  // unconditionally (Radius/Time/Property Type are legitimate to save even on a never-searched
  // property), but the recompute half is gated twice:
  //  - hasSavedComps, not hasSearched: see hasSavedComps's own comment above for why the recompute
  //    gate needs the narrower check. SearchCriteria renders on this panel unconditionally regardless
  //    of either flag, so without this check, blurring any field on a property with no saved comps
  //    fires a recompute that always 404s NO_SAVED_COMPS -- a wasted round trip on the common case,
  //    since most production properties have never been searched.
  //  - !hasRecomputeFieldError: the recompute body always carries all four scoring fields together
  //    (buildRecomputePayload), so a value currently failing its own on-screen validation in ANY one
  //    of them would poison every OTHER field's recompute too — the endpoint 400s the whole body —
  //    silently, until that one field is fixed. See hasRecomputeFieldError's own comment
  //    (features/arv/fieldLimits.js) for why this is the same check SearchCriteria uses to paint a
  //    red message, so "the screen shows no error" and "a recompute goes out" can never disagree.
  //    Only queueRecompute is skipped, not persistCriteriaFilters — an invalid Beds does not make an
  //    edited Radius any less worth saving. pendingRecompute is cleared on this branch too: it is
  //    the drain loop's only source of what to send, so an earlier, now-superseded edit left
  //    sitting in it would otherwise survive an invalid one and get sent later on its behalf.
  //  - the recompute body unchanged from the last one actually sent: Radius and Time blur through
  //    this same handler despite never appearing in buildRecomputePayload, so a Radius or Time edit
  //    alone reaches this point with the identical four scoring values already sitting on the
  //    server — sending it again is a full aggregation pass over every saved comp, and an arv.rev
  //    bump, for a response that cannot differ. lastSentRecomputeBody is the guard, not
  //    pendingRecompute (see its own comment for why that comparison would break coalescing) — and
  //    this branch also clears pendingRecompute rather than leaving it, for the same reason as the
  //    branch above: it may still hold an earlier, reverted edit the current one has overtaken.
  const handleCriteriaPersist = (updatedCriteria) => {
    persistCriteriaFilters(updatedCriteria)
    if (!hasSavedComps) return
    if (hasRecomputeFieldError(updatedCriteria)) {
      // Nothing valid to send right now. pendingRecompute is the drain loop's only source of what
      // to send next, so an earlier edit still sitting there from before this one turned invalid
      // would otherwise survive this decision and get sent later, describing a criteria state this
      // edit has already superseded.
      pendingRecompute.current = null
      return
    }
    const nextBody = JSON.stringify(buildRecomputePayload(updatedCriteria))
    if (nextBody === lastSentRecomputeBody.current) {
      // The current criteria's scoring fields already match the body of the request in flight or
      // just completed, so there is nothing left to reconcile. Clearing rather than leaving
      // whatever an earlier, since-reverted edit queued: that value describes a state the user has
      // moved away from, and re-queuing the current one here would only schedule an identical,
      // wasted resend once the in-flight request finishes.
      pendingRecompute.current = null
      return
    }
    queueRecompute(updatedCriteria)
  }

  // A plain setter, deliberately. Two separate defects met in the three lines that used to stand
  // here, and both of them were the `visible: 10` this no longer writes.
  //
  // The reset belonged to Load More. `visible` was that button's position, and a filter change made
  // the position meaningless. Show is a choice rather than a position: a user sitting on `All 48`
  // who types a price bound is narrowing the set they asked to see, and snapping the table back to
  // ten rows is not something any requirement asks for. A search still resets it, in handleSearch,
  // because a search replaces the comp set and the backend mirrors the same two keys.
  //
  // The reset also made the applied object differ from the persisted one. The Status select hands
  // the SAME object to onFiltersChange and to onFiltersBlur, so state got 10 while the server kept
  // the old number - silently, since the write ends in a .catch that only logs. The next reload
  // opened on a window the user never chose, which is how a legal `visible: 20` turned into the
  // illegal state normaliseShow now has to absorb. Applying and persisting the identical object is
  // what closes that, so this must stay free of transforms: anything that has to change a filter
  // before it is stored has to change it before it is persisted too.
  const handleFiltersChange = (newFilters) => setFilters(newFilters)

  // Fields a top-up is not allowed to move. The backend already guarantees it — an Active comp is
  // enriched through the ACTIVE_DISPLAY_FIELDS allow-list, which carries no ranking field (decisions
  // 6.10 and 6.11: the score is frozen at ranking, the adjusted price with it) — so this is the same
  // invariant asserted on the side that would show the damage. If a photo request ever did come back
  // with a different score, the row would reorder under the cursor and the ARV number in the metric
  // cards would move, both while the user is doing nothing but scrolling.
  //
  // `archived` is here for a different reason: it is the user's, not the server's. A top-up in
  // flight while the user excludes the row must not bring the row back.
  const FROZEN_ON_TOPUP = [
    'score', 'adjustments', 'status', 'archived',
    'price', 'sold_price', 'list_price', 'list_date', 'last_sold_date',
    'dom', 'distanceSubj',
  ]

  // Photos for Active comps outside the backend's enrichment window.
  //
  // The backend enriches the first ten rows of the ranking and returns the rest bare, because each
  // Active comp costs two provider calls against a ceiling of five per second — that narrowing is
  // the point of mr-03's backend commit. The rows past it get their photos here, one request each,
  // the first time they are actually on screen.
  //
  // The trigger is "a visible Active row has no photos", not "the user widened Show". Widening is
  // only one of the ways a bare row becomes visible: the price and sqft bounds persist across
  // searches and are applied BEFORE the window is cut, so a filtered table can open on Active comps
  // ranked far below tenth without the Show field ever being touched.
  //
  // Serial, not parallel: the provider limiter in reilm-be is one queue for the whole process at
  // five requests a second, shared with every other search running. Twenty rows firing at once would
  // not arrive faster, they would just sit in that queue ahead of somebody's search.
  const requestedTopUps = useRef(new Set())
  const topUpQueue = useRef([])
  const topUpRunning = useRef(false)

  // Stops the drain when the screen goes away. `Show -> All N` on a dense address queues every bare
  // Active row; navigating out mid-drain left the loop pulling the rest of that queue, and while
  // setComps on an unmounted component does nothing, each call still spends two provider requests
  // through the single process-wide limiter in reilm-be utils/realtorClient.js - an abandoned queue
  // draining behind somebody's live search, which is the shape ../knowledge_base/mistakes.md already
  // records about funnelling calls onto one limiter.
  //
  // Deliberately not an AbortController. Aborting the request in flight does not unwind the two
  // provider calls the backend has already queued for it, so it buys nothing against the cost this
  // is about; what costs is every request AFTER the one in flight, and stopping the loop stops those.
  //
  // One guard and not two. A second check after the await would only skip a setComps that is already
  // a no-op on an unmounted component, and the loop condition below re-reads this before every
  // request anyway - so no test could tell that check from its absence, which is the definition of
  // a line that should not be there.
  //
  // The priority lane stays where it is. resolveListingDetail passes no priority, so a search and a
  // top-up both sit in PRIORITY_BULK; promoting top-ups would put a sixty-row photo burst ahead of
  // another user's search, which is worse than the problem.
  const topUpsAlive = useRef(true)
  // Pauses the drain while the user's own search is in flight. Same reasoning as topUpsAlive, a
  // different case: an abandoned top-up costs two provider calls through the one process-wide
  // five-per-second queue in reilm-be utils/realtorClient.js, and a search takes 17-27 seconds -
  // so a queue left draining spends that whole window taking slots from the search the user is
  // sitting and waiting for. Neither existing trigger stops it: handleSearch touches neither comps
  // nor arvData until the promise resolves, so bareVisibleIds and searchedAt both stand still for
  // the entire wait, and the prune effect never runs.
  const searchInFlight = useRef(false)
  useEffect(() => {
    topUpsAlive.current = true
    return () => { topUpsAlive.current = false }
  }, [])

  // Reset when the comp set is replaced. A new search reuses propertyIds, and a comp that came back
  // bare in the previous analysis would otherwise be remembered as already asked for.
  useEffect(() => {
    requestedTopUps.current = new Set()
    topUpQueue.current = []
    confirmedArchivedRef.current = new Map()
    // Per-row archived:<id> banner keys are the one SAVE_KEY_* case a successful search cannot
    // self-heal on its own: the three shared keys get rewritten by the search itself, but nothing
    // else ever clears an archived entry left over from a row the fresh comp set no longer
    // describes -- guarded so an unrelated banner (criteriaFilters, filtersPersist) raised after
    // this search started is not wiped by a search that touches none of that failure's own keys.
    let clearedAny = false
    for (const key of Array.from(failedSavesRef.current)) {
      if (key.startsWith('archived:')) {
        failedSavesRef.current.delete(key)
        clearedAny = true
      }
    }
    if (clearedAny && failedSavesRef.current.size === 0) setSaveError(null)
  }, [arvData?.searchedAt])

  const runTopUps = async () => {
    if (topUpRunning.current) return
    topUpRunning.current = true
    try {
      while (topUpsAlive.current && !searchInFlight.current && topUpQueue.current.length) {
        const compId = topUpQueue.current.shift()
        try {
          const res = await reilmAPI.enrichArvComp(id, compId)
          const fresh = res.data?.data?.comp ?? null
          if (!fresh) continue
          setComps(prev => prev.map(c => c.propertyId === compId
            ? { ...c, ...fresh, ...Object.fromEntries(FROZEN_ON_TOPUP.map(k => [k, c[k]])) }
            : c))
        } catch (err) {
          // A failed request is not retried here, for the reason it never was: a comp with no photos
          // is a row with an empty gallery, which is the state it was already in, and retrying on
          // the spot would spend calls from the same ceiling on the case that just failed.
          //
          // What changed is how long the failure is remembered. Leaving every failure in
          // requestedTopUps made a single 429 permanent for the session: the row is off the queue
          // before the await, so the prune below can never reach it, and no gesture the user has -
          // widening Show, changing Status, opening Hidden and coming back - asks for it again. That
          // is the outcome the prune's own comment calls wrong, applied to the rows that failed.
          //
          // So a status the backend chose about THIS comp stays remembered, and everything else is
          // unmarked for one more attempt when the visible set next changes. Any 4xx this endpoint
          // returns except 429 (the shared rate ceiling, not an answer about the comp) is an answer:
          // asking again would get the same one. No response at all or a 5xx are not.
          //
          // This cannot loop. The effect below re-runs on a change of bareVisibleIds, which is a
          // JSON string of the visible bare ids - unmarking does not alter it, so nothing is
          // re-queued until the user moves the window or a row's photos land. One retry per gesture,
          // never per render.
          const status = err?.response?.status
          const answered = status >= 400 && status < 500 && status !== 429
          if (!answered) requestedTopUps.current.delete(compId)
        }
      }
    } finally {
      topUpRunning.current = false
    }
  }

  const handleShowChange = (value) => {
    const next = { ...filters, visible: value }
    setFilters(next)
    handleFiltersPersist(next)
  }

  const handleFiltersPersist = (updatedFilters) => {
    const gen = nextSaveGeneration(SAVE_KEY_FILTERS)
    reilmAPI.updatedPropertiesDetails(id, { 'arv.filters': updatedFilters })
      .then(() => markSaveOk(SAVE_KEY_FILTERS, gen))
      .catch(err => {
        console.error('filters persist', err)
        markSaveFailed(SAVE_KEY_FILTERS, err, gen)
      })
  }

  const addressDisplay = property ? [
    property.address?.street,
    property.address?.city,
    property.address?.state,
    property.address?.zip,
  ].filter(Boolean).join(', ') : ''

  // Moved into the header (Comment Set #1, ARV 1) from ArvMap.jsx, which drew this same string from
  // the same `arv.searchedAt` under the map. Format is carried over unchanged — en-US, short month,
  // 12-hour clock — so a relocated line is not also a reworded one.
  //
  // Named for the value rather than the whole line (`lastSearchLabel` before this fix): the label
  // "Last Search: " is now static JSX text so the line can always mount, with only this value
  // toggling between a formatted date and the empty-state dash. See the comment beside the render.
  const lastSearchValue = arvData?.searchedAt
    ? new Date(arvData.searchedAt).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      })
    : null

  // Search / Update Analysis. hasSearched is the analysis on the server rather than a flag of our
  // own: arv.searchedAt is written by searchArvService and by nothing else, so it survives a reload
  // and cannot drift from whether comps exist.
  const hasSearched = Boolean(arvData?.searchedAt)

  // The recompute's own precondition -- deliberately not hasSearched, and not folded into it.
  // hasSearched only says a search ran; searchArvService writes searchedAt in the same $set as
  // comps with no guard on comps being non-empty, so a search that comes back with zero comparables
  // leaves hasSearched true over an empty arv.comps, and recomputeArvService 404s NO_SAVED_COMPS on
  // exactly that. Reading hasSearched at the recompute gate let a field blur after such a search
  // fire a request that always failed, raising the shared save-error banner over an edit that saved
  // fine. Kept apart from hasSearched itself: that flag also drives showsStatusModule and the prop
  // SearchProgress renders on below, and a zero-comparable search changing what either of those
  // shows is a UI behaviour nobody asked for -- see handleCriteriaPersist for the one call site this
  // replaces.
  const hasSavedComps = Boolean(arvData?.comps?.length)

  // logic-auditor, second pass (09.09): the exact condition under which SearchProgress renders
  // something rather than null — `!searching && !hasSearched` is its own early return, and the
  // `hasSearched` prop it is actually given below is `hasSearched && !searchError`, not the raw
  // variable above. Kept here rather than inlined at the two call sites (the class string and the
  // prop below) so the two cannot drift apart the way the padding and the prop already had.
  const showsStatusModule = searching || (hasSearched && !searchError)
  const criteriaDirty = !sameSearchCriteria(criteria, searchedCriteria)

  // baseFiltered: every comp matching the filter row, before Show cuts the window. Feeds the Show
  // options and the window below. It fed the `N Results` pill too until Comment Set #1 item 9 took
  // that pill out, which is why nothing prints this number on screen any more.
  const baseFiltered = applyBaseFilters(comps, filters, criteria)

  // Slice before filtering archived, so included + excluded always equals the Show window.
  // This is the no-backfill rule his requirement states outright: "Selecting Top 10 and editing the
  // table should not grab the next comps. (i.e., at top 10, user excludes 2 comps, table remains at
  // 8 comps)". Excluding shrinks the included count; it never pulls the eleventh comp up into view.
  //
  // One normalised value feeds both the window below and the Show field, so the number the field
  // names and the number the table cuts at cannot disagree - the same reason displayOrder is one
  // function shared by two callers in reilm-be utils/arvFetch.js. They used to be computed
  // separately and did disagree on every saved filter set Load More had written.
  const showValue  = normaliseShow(filters.visible ?? 10, baseFiltered.length)
  const windowSize = showValue === SHOW_ALL ? baseFiltered.length : showValue
  const windowComps = baseFiltered.slice(0, windowSize)
  const includedComps = windowComps.filter(c => !c.archived)
  const excludedComps = windowComps.filter(c =>  c.archived)
  const displayedComps = filters.archivedView === 'archived' ? excludedComps : includedComps

  // Queue the rows that are on screen right now and have nothing to show in their gallery. Only
  // Active: Sold and Pending are enriched during the search without exception, because their status
  // and their adjusted price cannot be known without the detail call — a Sold comp arriving here
  // would mean something else went wrong, and the endpoint refuses it anyway (COMP_NOT_ACTIVE).
  //
  // Derived from state alone. This used to filter against requestedTopUps.current right here, during
  // render - while the ref that holds it is cleared by the effect above, which runs AFTER this
  // render. So the first render following a second search still saw the previous search's ids in the
  // ref, produced an empty string, and the effect never fired; the ref was then cleared with nothing
  // left to trigger it. RentCast ids are stable across searches, so every visible bare row took that
  // path: measured 0 top-up calls against 10 bare Active rows after a second search, self-healing on
  // the next unrelated render, which is what made it look like nothing was wrong. The dedup lives in
  // the effect now, where the ref is read after the reset rather than before it.
  //
  // JSON and not a comma-joined string. A RentCast propertyId IS an address —
  // `1954-Glenmar-Dr,-Decatur,-GA-30032` — so it carries two commas of its own, and 77 of 77 comps
  // on a live Decatur search do. Splitting that list on a comma shredded every id into three, and
  // the queue then asked the server to enrich `1954-Glenmar-Dr`, `-Decatur` and `-GA-30032`:
  // measured against the running backend 22.08, three PATCHes per row, every one a 404
  // COMP_NOT_FOUND, and the real comp never asked for at all. Photos for Active rows outside the
  // server's window never arrived, which is the whole feature the narrowed window pays for.
  //
  // Every test fixture used `act1` and `c0` for these ids, so 176 green tests said nothing about it.
  // That is the reason the fixtures below now carry real comma-bearing ids.
  const bareVisibleIds = JSON.stringify(
    displayedComps
      .filter(c => c.status === 'Active' && !(c.photos?.length > 0))
      .map(c => c.propertyId)
      .filter(Boolean)
  )

  useEffect(() => {
    const ids = JSON.parse(bareVisibleIds)
    const visible = new Set(ids)

    // Drop what has left the window before adding what has entered it. Narrowing `Show` back to
    // Top 10 mid-drain, typing a price bound, or opening the Hidden pill all take rows off screen
    // while this component stays mounted, and the queue only ever grew: measured 4 requests where
    // one was owed, and on the live 22.08 Decatur search - 77 comps, 40 Active, server window 10 -
    // that is up to thirty abandoned rows and sixty provider calls. The photos would be kept, so
    // this is not wasted work; it is twelve seconds of a shared five-per-second limiter spent ahead
    // of somebody else's live search, which is the same cost topUpsAlive exists to stop at unmount.
    // The trigger is "a visible Active row has no photos", so a row nobody is looking at has no
    // claim on the queue whether the screen went away or the row did.
    //
    // Unmarking as well as dequeuing: a pruned row was never actually asked for, so leaving it in
    // requestedTopUps would mean widening Show again never fetches it. The row in flight is already
    // off the queue and cannot be recalled - that one request stands.
    for (const pid of topUpQueue.current) {
      if (!visible.has(pid)) requestedTopUps.current.delete(pid)
    }
    topUpQueue.current = topUpQueue.current.filter((pid) => visible.has(pid))

    let queued = false
    for (const pid of ids) {
      if (requestedTopUps.current.has(pid)) continue
      requestedTopUps.current.add(pid)
      topUpQueue.current.push(pid)
      queued = true
    }
    if (queued) runTopUps()
    // A string and not the array: the dependency has to compare by value, or an array rebuilt on
    // every render restarts this on every render. A re-run with the same visible rows finds every id
    // already in requestedTopUps and queues nothing.
    //
    // searchedAt is the second dependency because the string alone can stand still across a search:
    // a top-up that failed leaves its row bare, so the same ids are still on screen when the fresh
    // result arrives, and the reset above has just made them askable again. Without it those rows
    // would keep the failure of the previous search forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bareVisibleIds, arvData?.searchedAt])

  // Pill counts from the window — included + excluded = the Show window
  const visibleCount = includedComps.length
  const hiddenCount  = excludedComps.length

  // "When the user edits (excludes/restores comps) the table, the field value should change from
  // Top x to Custom". Derived from the comps rather than stored: an exclusion IS the edit, so there
  // is nothing a separate flag could say that this does not, and a flag would have to survive a
  // reload through arv.filters, whose key set is fixed in the model. It also falls back on its own —
  // a search clears every archived flag, so the field reads Top 10 again with no reset to write.
  //
  // Custom is display-only and never selectable, which is his answer OC 3: picking Top 10 again
  // returns the window to ten and leaves the exclusions exactly where they are.
  const isCustomShow = comps.some(c => c.archived)

  // The three metric cards describe the comps INCLUDED in the analysis, never the excluded ones.
  // Reading them off displayedComps made every card describe whichever pill happened to be open:
  // with Hidden selected, the estimate, Comp Quality and Average DOM were computed from the archived
  // comps alone — the ones the user removed for not fitting. Measured 20.08: four included comps
  // averaging $300K repainted as $900K on one click of Hidden. filters.archivedView is persisted, so
  // a property last left on that pill opened with the excluded number standing as its ARV. Spec:
  // "based on the adjusted comps included in your analysis" (files/sow2/spec/arv.md, section 8).
  // metrics.compCount included: the caption it feeds is "Based on N adjusted comps", and the number
  // was `statusOnlyComps.length` — status filter only, so it counted archived comps, ignored the
  // price and sqft bounds and ignored the window. On the 11-comps-one-archived case below it read
  // 11 against an estimate averaged over 9. It renders nowhere today (EstimateMetrics.jsx:48 emits
  // that label with no number at all), which is precisely why it had to be fixed now rather than
  // deferred: mr-03's job there is wiring a number into an existing label, and it would have wired
  // the wrong one. Which included comps it counts is settled at adjustedComps below.
  //
  // Average over the comps that actually carry a number. `?? 0` counted a missing adjusted price as
  // a $0 sale and still divided by the full length, so one comp with adjustments: null dragged the
  // estimate down by its whole share — four comps at $300K plus one null gave $240K. That shape is
  // reachable: reilm-be arvCompSchema declares `adjustments` with `default: null`, and arvFetch.js
  // carries its own comment on this exact defect where it computes the Active bucket's adjusted
  // prices early "so a single comp missing it drags the headline ARV down". The score average got
  // the same treatment for consistency, not because it was reachable: arvCompSchema gives `score` a
  // `default: 0` and calcScore always returns a bounded number, so nothing in the backend produces a
  // null score today. averageDom below always filtered its nulls out via this same helper; these two
  // did not.
  //
  // Number.isFinite rather than `!= null`, as a tightening and not a reproduced fix: calcAdjust
  // returns `Math.round((price + total) / 100) * 100`, which is NaN when handed a comp with no sold
  // price, and NaN would survive a null check and poison the whole average. It should not reach
  // here — JSON has no NaN literal, so anything of that shape arrives as null — but the predicate
  // costs nothing and every legitimate value here is a finite number.
  const mean = (rows, pick) => {
    const vals = rows.map(pick).filter(v => Number.isFinite(v))
    return vals.length ? vals.reduce((sum, v) => sum + v, 0) / vals.length : null
  }

  // The divisor, not the row count. `mean` averages only the comps carrying a finite adjusted price,
  // and the caption this number feeds reads "Based on N adjusted comps" - naming a number the sum was
  // not divided by is a false statement on the screen, and the one thing the reader cannot check.
  // Measured 22.08: 56 of 1062 saved comps hold `adjustments: null` (reilm-be arvCompSchema declares
  // it `default: null`), all of them unexcluded, and on 4 of 34 saved analyses the two numbers part -
  // one reads "Based on 10 adjusted comps" over an average of a single comp, another 10 against 4.
  //
  // files/sow2/spec/arv.md section 9 settled the value as the actual count of included comps. That
  // sentence was rejecting the mock constants 48 / 10 / 20, not adjudicating a null; this is still
  // the included comps, narrowed to the ones the caption's own words name. Section 9 records it.
  //
  // Zero of them cannot render "Based on 0": no finite adjustment means arvMean is null, liveArv is
  // null, and the whole block is behind that guard in EstimateMetrics.
  //
  // Between one and n-1 is the visible case, and it is deliberate rather than overlooked. Every other
  // number on the screen counts the rows: both pills, the rows drawn, the Comp Quality average and
  // the Average DOM all read ten where this reads one. They are answering a different
  // question - how many comps are in the analysis, against how many of them the sum could be taken
  // over - and the caption is the only one whose words name the second. The alternative was a caption
  // agreeing with its neighbours and disagreeing with the number printed above it.
  //
  // `> 0` and not `Number.isFinite`, which is mr-02 finishing what mr-03 started here. The finite
  // check closed null and NaN and left zero, and zero is exactly what a comp with no base price
  // used to arrive with: reilm-be's calcAdjust coerced the missing price to 0 in every term and
  // returned 0, `Number.isFinite(0)` is true, so that comp went into the sum and into the divisor.
  // Measured 26.08 on his own analysis of 55 comps, one Sold comp with no sale price from the
  // provider: 254 051 against 258 756, an ARV 4 705 low (files/sow2-notes.md section 17).
  //
  // Fixed on the backend as well, and this is not the same fix twice. compAdjustments now answers
  // null, so nothing new arrives as a zero — but the two analyses already saved with one keep it
  // until their next search, and this is what makes them read correctly in the meantime. A negative
  // is caught by the same comparison, and it says the same thing: not a price a house sold for.
  const adjustedComps = includedComps.filter(c => Number.isFinite(c.adjustments) && c.adjustments > 0)

  const arvMean = mean(adjustedComps, c => c.adjustments)
  const liveArv = arvMean == null ? null : Math.round(arvMean)

  const avgScore = mean(includedComps, c => c.score)
  const { label: qualityLabel, sub: qualitySub } = getCompQuality(avgScore)

  // Average DOM is a formula change, not a label rename: this used to sort the included comps'
  // `dom` values and take the middle one (or the mean of the middle two); it now reuses the same
  // `mean` helper the ARV and Comp Quality cards above are built on, which is the Number.isFinite
  // tightening already argued for at the comment above rather than the looser `!= null` the median
  // version filtered with.
  const domMean = mean(includedComps, c => c.dom)
  const averageDom = domMean == null ? null : Math.round(domMean)

  const metrics = {
    arv: liveArv,
    compCount: adjustedComps.length,
    qualityLabel,
    qualitySub,
    averageDom,
  }


  // Same three renders as PropertyDashboard's `!property` branch: nothing under ExpiredModal when
  // locked, otherwise its message -- never the default criteria with a Search that would overwrite
  // the saved analysis.
  if (loadError === 'locked') {
    return (
      <PageShell>
        <AppNavbar />
      </PageShell>
    )
  }
  if (loadError) {
    return (
      <PageShell>
        <AppNavbar />
        <div className="flex items-center justify-center py-20 text-red-400 text-[14px]">
          {loadError === 'load_failed' ? 'Could not load this property, try again' : 'Property not found.'}
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <AppNavbar />
      <div className="min-h-screen bg-brand-pageFill">
        <div className="mt-[24px] flex flex-col gap-y-[20px] pb-[60px]">

          {/* Header. Comment Set #3, item 1 (letter 57 from Cong, 04.09): "The search statuses
              appear to move the Search Criteria downwards." SearchProgress's four-pill stack is
              taller than this column, so as a flex sibling of it under the old `items-start
              justify-between` row, it drove the row's own height and Search Criteria — the row's
              next sibling in the page's flex-col — moved down with it every time a search started
              or finished. Absolute instead of flex: the indicator now sits outside this column's
              box model entirely (`relative` below is only for that), so nothing it renders can
              change where Search Criteria sits. `mb-[4px]` stacks on the page body's own
              `gap-y-[20px]` to make 24px specifically between this column and Search Criteria
              (ARV 2.5) without touching the 20px the other sections below still rely on. */}
          <div className="relative mb-[4px]">
          {/* code-reviewer, confirmed with a real Chrome reproduction (09.09): the status module
              (`absolute top-0 right-0` below) sits outside this column's box model, so nothing here
              stops the address line running under it. Measured at 768 with a 78-char address, ~87px
              of text including the zip painted under the opaque card — the existing 36-char fixture
              never reached that column, which is why nothing had caught it.

              `pr-[300px]` reserves the module's own horizontal band on this column, sized to its
              widest state rather than measured against one render: border 1px×2 + `px-[23.5px]`×2
              (47) + text, and the widest state this module ever draws is its own collapsed block
              (SearchProgress.jsx), not the four-pill search stack (~200px) the report measured.

              logic-auditor, second pass (09.09), on the number this comment used to cite for the
              old single-line pill: 241px is `arv.md:65` / `state-width-matrix.md:153`, the Figma
              frame width for `search-in-progress/search-complete-collapsed/btn_75` (exported
              18.08) — this file's own word for that is "drawn", not measured, and the same spec
              file records an ~11px real-vs-Figma gap on a sibling pill in this exact app (241.8
              real vs 230.5 drawn, `state-width-matrix.md:149`), so citing 241 as if it were a DOM
              number was already contradicted by evidence sitting one section away.

              Re-measured in real Chrome after the collapsed state changed from one line to two
              stacked rows: the module got narrower, not wider — two short lines beat one long one
              on width. Outer width (border + moduleCls padding, no other change) is 211.72px for a
              2-digit count ("Found 37 Relevant Comps"), 218.53px for 3-digit, and 225.89px for an
              unrealistic 4-digit count — every case clears 300px with at least 74px to spare, more
              margin than the single-line pill ever had. Height went the other way for the collapsed
              state: 83.5px against the old line's ~46px, and it still sits inside this column's own
              natural height (114px at 1280 and 768 with the current header) with about 30px to
              spare. That margin belongs to the collapsed state only — the four-pill searching stack
              this same module renders is 141px tall (moduleCls padding + four 23px pills + gaps),
              27px past this column's own bottom at both widths, so it already runs into the Search
              Criteria gap below. Pre-existing, not touched by this change (PHASES/searching markup
              is untouched), and worth a look on its own. Padding on this column, not a max-width on
              the address alone, so the reservation also covers the title row and the Last Search
              line without three separate numbers to keep in sync.

              No test can re-run that measurement — jsdom has no layout engine — so if the collapsed
              block's own text or layout changes again, or a comp count is ever allowed past four
              digits, re-measure both numbers (width against 300px, height against this column)
              rather than extrapolating from the ones above.

              Applied only when `showsStatusModule` is true (derived above from the same three
              inputs SearchProgress itself gates its own render on): SearchProgress returns null,
              not a pill, when a property has never been searched or its last search just failed
              (`!searching && !hasSearched` in SearchProgress.jsx, with `hasSearched` there already
              meaning `hasSearched && !searchError`) — so in those two states there is nothing here
              for the reservation to protect against, and holding 300px anyway just narrows the
              address for no reason. logic-auditor measured this at 768 with a long address in the
              never-searched state (09.09): 46 characters fit under the unconditional padding, 80
              fit once it is conditional — not re-measured this round, since the class toggle itself
              is what changed and jsdom's assertion on it needs no browser to confirm.

              Toggling this class cannot revive Comment Set #3 item 1 (the status module's own
              height used to push Search Criteria down): the address line carries `truncate`, so it
              is one line regardless of how wide this column is, and narrowing or widening it changes
              no element's height — only where the ellipsis falls. Nothing downstream of this column
              can move because of it. */}
          <div className={`flex flex-col gap-y-[8px] ${showsStatusModule ? 'pr-[300px]' : ''}`}>
            <BackLink to={`/property/${id}`}>Back to Property Dashboard</BackLink>
            <div className="flex items-center gap-x-[8px]">
              {/* Comment Set #1, ARV 3: ExtraBold. 20/800 on heading_11/.../Frame 398, not the 700 that
                  `font-bold` gives. */}
              <h1 className="text-[20px] font-extrabold text-brand-dark">ARV Analysis</h1>
              <div className="relative group">
                {/* Letter 106, D5: #328C12 on hover (brand-hover), held while its tooltip is open. */}
                <span className="text-[13px] text-brand-secondary group-hover:text-brand-hover underline cursor-default">How to Use</span>
                {/* Three paragraphs, literal from the canvas block `Tooltips ARV Analysis`. The
                    numbered list that stood here was the previous developer's paraphrase and named
                    the card by its old title, Comps Strength. Figma separates these with U+2028, a
                    line break rather than a space, so they are three paragraphs and not one.

                    Comment Set #1, ARV 4: "start below the text; it should not appear above or cover
                    the top navbar". It opened upward off `bottom-full`, and measured 02.09 at 1280
                    that put its top at **y -66** — over the navbar, whose bottom is 72, and 66px past
                    the top of the window, so the whole first paragraph was unreachable. `top-full`
                    with the same 8px offset puts it under the link instead.

                    ARV 4.1, "add spacings between each paragraph": the gap is `group-hover:flex`
                    now, not `flex`. This div carried `hidden group-hover:block` beside a plain
                    `flex flex-col gap-y-[10px]`, and the variant wins twice over — `.group:hover
                    .group-hover\:block` is two classes against one and sits later in the sheet
                    (rule 767 against 189, read out of the live CSSOM). So the shown tooltip was
                    `display: block`, `flex-col` and the gap were inert, and the three paragraphs
                    ran together. The gap was written; it had never once applied. */}
                <div
                  className="absolute z-20 hidden group-hover:flex flex-col gap-y-[10px] top-full left-0 mt-[8px] bg-white rounded-[8px] p-[14px] text-[13px] leading-[1.5] shadow-lg pointer-events-none"
                  style={{ width: 450, color: '#1A2A10' }}
                >
                  <div>
                    Search for comparable properties, review the top matches, and exclude any that don’t fit. Your ARV Estimate, Comp Quality, and Average DOM update automatically.
                  </div>
                  <div>
                    When everything looks right, share the analysis with buyers, partners, or lenders.
                  </div>
                  <div>
                    Tip: For the most accurate ARV, confirm the property’s after-renovation details—such as sqft, beds, and baths—before searching.
                  </div>
                </div>
              </div>
            </div>
            {loading
              // 24px, not the 21px this carried under `text-[14px]`: mr-12 took the address to
              // `text-[20px] leading-[24px]`, and the skeleton was left on the old line box.
              ? <div className="h-[24px] w-[220px] bg-brand-muted rounded animate-pulse" />
              // `truncate` + `title`: the same pairing `features/arv/columns.js` uses for the
              // Property cell, which hits this exact problem on a narrower box. Flat on the `<p>`
              // itself rather than a nested span — one string, one styled run, nothing for a second
              // element to buy here. `title` carries the untruncated address so nothing is silently
              // lost, same rule the columns.js comment states for its own truncated cell.
              : <p className="text-[20px] font-normal leading-[24px] text-brand-dark truncate" title={addressDisplay}>{addressDisplay}</p>
            }
            {/* Comment Set #1, ARV 1 and 2.4: relocated here from under the map. ArvMap.jsx drew
                this same string from the same `arv.searchedAt` prop, and SearchCriteria.jsx carried
                a comment explaining why it deliberately did not — both stale now that this is the
                one place it renders; both updated alongside this. The 8px gap from Address is free:
                this is the fourth child of the column above, which already carries `gap-y-[8px]`.

                logic-auditor, confirmed (09.09): this used to mount only once `searchedAt` existed,
                so a property's first search added a fourth child to this column the instant it
                resolved and Search Criteria — this column's own next sibling — measured 24.5px lower
                than it had a moment before. Same user-visible complaint Comment Set #3 item 1 named
                ("Search Criteria moved"), a different trigger: that one was the status module's
                height leaking into this column's own layout, this one was this column's own child
                count changing. The line is now always mounted once loading finishes, so the column's
                height never depends on whether a search has ever run.

                qa-verifier (09.09): a visible em-dash here on a never-searched property is itself new
                content — before mr-12 this whole line did not render at all in that state, so a dash
                is not neutral. His own stated rule elsewhere (letter 50, Add Property item 1: "EM
                dashes should not appear before entering a property... only after... and results
                return no value") is the same principle applied to a different field. `invisible`
                keeps the character in the box so the line box's dimensions do not depend on whether
                there is a value — the height invariance above still holds without a browser-only
                layout engine to fall back on in jsdom — but paints nothing, so a never-searched
                property shows exactly the label and no dash, matching what rendered before mr-12. */}
            {!loading && (
              <p className="text-[11px] font-normal text-brand-secondary">
                Last Search: {lastSearchValue ?? <span className="invisible">—</span>}
              </p>
            )}
          </div>

            {/* Comment Set #1, ARV 5: "Update search progress states to one UI component and
                relocate to the top-right of the page per Figma". The export draws it right-aligned:
                `arv_search/in_use` holds the four pills at y 1200.5 to 1319.5, and their four
                different widths (171, 172, 178, 163) all end on the same x, 9881.5, against a
                content edge of 9899.5 — SearchProgress.jsx carries the rest of that reading. `right-0`
                on a `relative` ancestor that spans the same width the old `justify-between` row did
                reaches the same edge.

                Hidden while an error is on screen. The pill would still be describing the analysis
                the table is showing — the previous one, since a failed search replaces nothing —
                but next to "Search failed" it reads as a verdict on the run that just failed. */}
            {!loading && (
              <div className="absolute top-0 right-0">
                <SearchProgress
                  searching={searching}
                  hasSearched={hasSearched && !searchError}
                  compCount={baseFiltered.length}
                />
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex flex-col gap-y-[16px]">
              <div className="h-[160px] bg-brand-muted rounded-2xl animate-pulse" />
              <div className="h-[600px] bg-brand-muted rounded-2xl animate-pulse" />
              <div className="h-[400px] bg-brand-muted rounded-2xl animate-pulse" />
            </div>
          ) : (
            <>
              {/* Search Criteria */}
              <SearchCriteria
                criteria={criteria}
                onCriteriaChange={setCriteria}
                onCriteriaBlur={handleCriteriaPersist}
                onSearch={handleSearch}
                searching={searching}
                hasSearched={hasSearched}
                canSearch={!hasSearched || criteriaDirty}
              />

              {searchError && (
                <p className="text-[12px] text-red-500">{searchError}</p>
              )}

              {/* Same text-[12px]/text-red-500/<p> pattern as this screen's own searchError banner
                  directly above -- not PropertyDashboard.jsx's (text-red-400 text-[14px]), a
                  different banner on a different screen. No Figma frame for this state; placed here
                  (in-flow, sized like searchError above it) rather than the header, which is
                  absolutely positioned with a measured pr-[300px] reservation this would disturb. */}
              {saveError && (
                <p className="text-[12px] text-red-500">{saveError}</p>
              )}

              {/* Map */}
              <div className="relative">
                <ArvMap
                  coordinates={property?.address?.coordinates}
                  comps={displayedComps}
                  onListingClick={(comp) => {
                    const idx = displayedComps.findIndex(c => c.propertyId === comp.propertyId)
                    navigate(`/property/${id}/arv/listing/${idx}`, { state: { comp, filteredComps: displayedComps, compIndex: idx } })
                  }}
                  onGalleryOpen={handleGalleryOpen}
                  onToggleArchived={handleToggleArchived}
                />
                {searching && (
                  <div className="absolute inset-0 bg-white/80 rounded-2xl z-10 pointer-events-none animate-pulse" />
                )}
              </div>

              {/* Filter row — full width */}
              <ComparableFilterRow
                filters={filters}
                criteria={criteria}
                onCriteriaChange={setCriteria}
                onCriteriaBlur={handleCriteriaPersist}
                onFiltersChange={handleFiltersChange}
                onFiltersBlur={handleFiltersPersist}
                showOptions={showOptions(baseFiltered.length)}
                showValue={showValue}
                isCustomShow={isCustomShow}
                onShowChange={handleShowChange}
              />

              {/* Grid + Sidebar */}
              <div className="flex gap-x-[15px]">

                {/* Left — grid */}
                <div className="relative flex flex-col gap-y-[16px] border border-brand-fieldBdr rounded-2xl " style={{ minwidth: 885, height:'100%' }}>
                  <div className="bg-white rounded-2xl overflow-hidden">
                    <ComparableGrid
                      rows={displayedComps}
                      onRowClick={(row) => {
                        const idx = displayedComps.findIndex(c => c.propertyId === row.propertyId)
                        navigate(`/property/${id}/arv/listing/${idx}`, { state: { comp: row, filteredComps: displayedComps, compIndex: idx } })
                      }}
                      onToggleArchived={handleToggleArchived}
                      onGalleryOpen={handleGalleryOpen}
                    />
                    {searching && (
                      <div className="absolute inset-0 bg-white/80 rounded-2xl z-10 pointer-events-none animate-pulse" />
                    )}
                  </div>
                </div>

                {/* Right — estimate sidebar */}
                <div>
                  <EstimateMetrics
                    metrics={metrics}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onFiltersBlur={handleFiltersPersist}
                    visibleCount={visibleCount}
                    hiddenCount={hiddenCount}
                  />
                </div>
              </div>
              
            </>
          )}

        </div>
      </div>
      {galleryPhotos && (
        <GalleryModal
          photos={galleryPhotos}
          id={galleryCompId}
          onClose={() => { setGalleryPhotos(null); setGalleryCompId(null) }}
        />
      )}
    </PageShell>
  )
}
